import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { formatDateTime } from '@/lib/i18n';
import { api } from '@/lib/api';
import { useCurrency } from '@/Hooks/useCurrency';
import { usePermission } from '@/Hooks/usePermission';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge, toneForStatus } from '@/components/ui/badge';
import { Dialog } from '@/components/ui/dialog';
import { Field, Select } from '@/components/ui/field';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { TD, TH, THead, TR, Table, TableWrap } from '@/components/ui/table';
import {
  COUPON_STATUSES,
  PROMOTION_TYPES,
  type Coupon,
  type Promotion,
  type PromotionType,
  apiError,
} from './lib';

const BLANK = {
  code: '',
  promotionId: '',
  customerId: '',
  discountType: 'PERCENTAGE' as PromotionType,
  discountValue: '10',
  minSpend: '',
  maxRedemptions: '1',
  expiresAt: '',
};

export default function CouponsTab({ promotions }: { promotions: Promotion[] }) {
  const { t, i18n } = useTranslation();
  const { can } = usePermission();
  // A coupon's fixed discount and its minimum spend are money in the branch's
  // currency; the percentage variants are not, and stay plain numbers.
  const { code: currency, format: formatMoney, toMoney, decimals } = useCurrency();
  const moneyStep = decimals === 0 ? '1' : `0.${'0'.repeat(decimals - 1)}1`;
  // I-06 — issuing and killing a discount code is no longer a side effect of
  // being allowed to edit a customer record.
  const canIssue = can('coupons.create');
  const canUpdate = can('coupons.update');
  const canDisable = can('coupons.disable');

  const [rows, setRows] = useState<Coupon[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState('');

  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api
      .get<{ data: Coupon[] }>('/coupons', {
        params: { limit: 100, status: status || undefined },
      })
      .then(({ data }) => setRows(data.data ?? []))
      .catch((e) => setError(apiError(e, t('crm.coupons.loadFailed'))))
      .finally(() => setLoading(false));
  }, [status, t]);

  useEffect(load, [load]);

  const submit = async () => {
    setSaving(true);
    const linked = !!form.promotionId;
    try {
      await api.post('/coupons', {
        code: form.code || undefined,
        promotionId: form.promotionId || undefined,
        customerId: form.customerId || undefined,
        // A coupon either unlocks a promotion or carries its own discount.
        discountType: linked ? undefined : form.discountType,
        discountValue: linked
          ? undefined
          : form.discountType === 'FIXED_AMOUNT'
            ? toMoney(form.discountValue)
            : form.discountValue,
        minSpend: form.minSpend ? toMoney(form.minSpend) : undefined,
        maxRedemptions: Number(form.maxRedemptions) || 1,
        expiresAt: form.expiresAt ? new Date(form.expiresAt).toISOString() : undefined,
      });
      toast.success(t('crm.coupons.issued'));
      setFormOpen(false);
      setForm(BLANK);
      load();
    } catch (e) {
      toast.error(apiError(e, t('crm.coupons.saveFailed')));
    } finally {
      setSaving(false);
    }
  };

  const act = async (coupon: Coupon, action: 'expire' | 'disable') => {
    try {
      if (action === 'expire') await api.post(`/coupons/${coupon.id}/expire`, {});
      else await api.delete(`/coupons/${coupon.id}`);
      toast.success(t(`crm.coupons.${action}d`));
      load();
    } catch (e) {
      toast.error(apiError(e, t('crm.coupons.saveFailed')));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Select
          className="w-48"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          aria-label={t('crm.coupons.status')}
        >
          <option value="">{t('crm.coupons.allStatuses')}</option>
          {COUPON_STATUSES.map((value) => (
            <option key={value} value={value}>
              {t(`crm.couponStatus.${value}`)}
            </option>
          ))}
        </Select>
        {canIssue && <Button onClick={() => setFormOpen(true)}>{t('crm.coupons.issue')}</Button>}
      </div>

      {loading ? (
        <LoadingState label={t('common.loading')} />
      ) : error ? (
        <ErrorState message={error} onRetry={load} />
      ) : rows.length === 0 ? (
        <EmptyState title={t('crm.coupons.empty')} hint={t('crm.coupons.emptyHint')} />
      ) : (
        <TableWrap>
          <Table>
            <THead>
              <tr>
                <TH>{t('crm.coupons.code')}</TH>
                <TH>{t('crm.coupons.discount')}</TH>
                <TH>{t('crm.coupons.redemptions')}</TH>
                <TH>{t('crm.coupons.expiresAt')}</TH>
                <TH>{t('crm.coupons.status')}</TH>
                <TH className="text-end">{t('catalog.items.actions')}</TH>
              </tr>
            </THead>
            <tbody>
              {rows.map((coupon) => (
                <TR key={coupon.id}>
                  <TD>
                    <code className="font-medium">{coupon.code}</code>
                    {coupon.promotionId && (
                      <div className="text-xs text-ink-muted">
                        {t('crm.coupons.unlocksPromotion', {
                          name:
                            promotions.find((p) => p.id === coupon.promotionId)?.name ??
                            coupon.promotionId,
                        })}
                      </div>
                    )}
                  </TD>
                  <TD>
                    {coupon.discountType
                      ? `${t(`crm.promotionType.${coupon.discountType}`)} · ${
                          coupon.discountType === 'PERCENTAGE'
                            ? `${coupon.discountValue ?? '0'}%`
                            : formatMoney(coupon.discountValue ?? '0')
                        }`
                      : '—'}
                    {coupon.minSpend && (
                      <div className="text-xs text-ink-muted">
                        {t('crm.coupons.minSpend', {
                          amount: formatMoney(coupon.minSpend),
                        })}
                      </div>
                    )}
                  </TD>
                  <TD>
                    {coupon.redemptionCount} / {coupon.maxRedemptions}
                  </TD>
                  <TD className="text-ink-muted">
                    {coupon.expiresAt ? formatDateTime(coupon.expiresAt, i18n.language) : '—'}
                  </TD>
                  <TD>
                    <Badge tone={toneForStatus(coupon.status)}>
                      {t(`crm.couponStatus.${coupon.status}`)}
                    </Badge>
                  </TD>
                  <TD className="text-end">
                    <div className="flex flex-wrap justify-end gap-2">
                      {canUpdate && coupon.status === 'ACTIVE' && (
                        <Button size="sm" variant="outline" onClick={() => act(coupon, 'expire')}>
                          {t('crm.coupons.expire')}
                        </Button>
                      )}
                      {canDisable && coupon.status !== 'DISABLED' && (
                        <Button size="sm" variant="danger" onClick={() => act(coupon, 'disable')}>
                          {t('crm.coupons.disable')}
                        </Button>
                      )}
                    </div>
                  </TD>
                </TR>
              ))}
            </tbody>
          </Table>
        </TableWrap>
      )}

      <Dialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        title={t('crm.coupons.issue')}
        description={t('crm.coupons.issueHint')}
        footer={
          <>
            <Button variant="outline" onClick={() => setFormOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={submit} disabled={saving}>
              {t('common.save')}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Field label={t('crm.coupons.code')} hint={t('crm.coupons.codeHint')}>
            <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} />
          </Field>
          <Field label={t('crm.coupons.promotion')} hint={t('crm.coupons.promotionHint')}>
            <Select
              value={form.promotionId}
              onChange={(e) => setForm({ ...form, promotionId: e.target.value })}
            >
              <option value="">{t('crm.coupons.standalone')}</option>
              {promotions.map((promotion) => (
                <option key={promotion.id} value={promotion.id}>
                  {promotion.name}
                </option>
              ))}
            </Select>
          </Field>

          {!form.promotionId && (
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label={t('crm.coupons.discountType')}>
                <Select
                  value={form.discountType}
                  onChange={(e) =>
                    setForm({ ...form, discountType: e.target.value as PromotionType })
                  }
                >
                  {PROMOTION_TYPES.map((value) => (
                    <option key={value} value={value}>
                      {t(`crm.promotionType.${value}`)}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field
                label={t('crm.coupons.discountValue')}
                hint={
                  form.discountType === 'FIXED_AMOUNT'
                    ? t('currency.amountIn', { code: currency })
                    : undefined
                }
              >
                <Input
                  type="number"
                  inputMode="decimal"
                  min="0"
                  step={form.discountType === 'FIXED_AMOUNT' ? moneyStep : '0.01'}
                  value={form.discountValue}
                  onChange={(e) => setForm({ ...form, discountValue: e.target.value })}
                />
              </Field>
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label={t('crm.coupons.minSpendLabel')}
              hint={t('currency.amountIn', { code: currency })}
            >
              <Input
                type="number"
                inputMode="decimal"
                min="0"
                step={moneyStep}
                value={form.minSpend}
                onChange={(e) => setForm({ ...form, minSpend: e.target.value })}
              />
            </Field>
            <Field label={t('crm.coupons.maxRedemptions')}>
              <Input
                type="number"
                min="1"
                value={form.maxRedemptions}
                onChange={(e) => setForm({ ...form, maxRedemptions: e.target.value })}
              />
            </Field>
          </div>

          <Field label={t('crm.coupons.expiresAt')}>
            <Input
              type="datetime-local"
              value={form.expiresAt}
              onChange={(e) => setForm({ ...form, expiresAt: e.target.value })}
            />
          </Field>
        </div>
      </Dialog>
    </div>
  );
}
